import { Result as R } from '@puzzlemania/shared';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import { POWERUP_REASON_PREFIX } from '../constants/economy';
import { effectAvailableOn } from './powerup-effects';
import { isPowerupUnlocked, powerupRegistry, powerupsUnlockingAt, } from './powerup-registry';
/**
 * `blockjam_powerup_...` reason id for a use. Ids are camelCase in config
 * (`colorDelete`); the economy ledger and the analytics schema are snake_case,
 * so they are converted rather than mixed.
 */
export function powerupReasonId(id) {
    return `${POWERUP_REASON_PREFIX}${id.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()}`;
}
export class PowerupController {
    constructor(_config, _deps) {
        this._config = _config;
        this._deps = _deps;
        /** Uses spent per power-up in the CURRENT level; cleared by `startLevel`. */
        this._used = new Map();
        this._level = 1;
        /**
         * Module 65 (WP-65.F1) — the puzzle ordinal of the current track, which is
         * what unlocks are measured against; `_level` stays the effective level for
         * the analytics rows that describe difficulty. Defaults to the level in
         * `startLevel` for a caller with no track (a single ladder, where they agree).
         */
        this._puzzle = 1;
        /** Module 77 (D4) — the track the unlock rule is asked about (`unlockPuzzleFor`). */
        this._track = undefined;
        /**
         * Unlocks already announced to analytics. Session-scoped on purpose: the
         * persisted `seenUnlocks` key namespace belongs to the WP-35.22 unlock popup,
         * and having two features mark the same key would mean whichever ran first
         * silently suppressed the other. Replaying an unlock level in one session
         * fires nothing extra; a fresh session at that level is the only repeat, and a
         * resumed save normally starts above it.
         */
        this._announced = new Set();
        /**
         * Module 63 (WP-63.E5) — free uses CONSUMED, per id, for the whole life of
         * the player. Restored from the save on load and written back by the
         * plugin's `_commitState`, which is what makes the grant once-ever rather
         * than once-per-load (§4 divergence 4: the Module 45 lesson, where a rebuilt
         * wallet JSON silently re-granted). Never reset by `startLevel`.
         */
        this._freeUsed = new Map();
        /**
         * Module 118 (WP-118.2) — free uses for ANY power-up (the daily reward's
         * day-7 ticket). Spent only after the power-up's own free grant is gone, so
         * the ticket is never wasted on a use that was free anyway. One shared pool:
         * every button shows it, and whichever power-up is used first spends it.
         * Player-wide, restored from the save's top-level `bonusFreeUses`.
         */
        this._bonusFree = 0;
    }
    /** The definitions this controller was built from, in config order. */
    get definitions() {
        return powerupRegistry(this._config, this._track);
    }
    get level() {
        return this._level;
    }
    /**
     * Enter a level: reset every per-level counter and announce anything that
     * unlocks exactly here. Call this on generate/next/restart/jump — the cap is
     * per level, so a restart genuinely hands the uses back.
     */
    startLevel(levelNumber, puzzle = levelNumber, track) {
        this._level = levelNumber;
        this._puzzle = puzzle;
        this._track = track;
        this._used.clear();
        for (const id of powerupsUnlockingAt(this._config, puzzle, track)) {
            if (this._announced.has(id))
                continue;
            this._announced.add(id);
            this._deps.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_UNLOCKED, {
                powerup_id: id,
                level_id: levelNumber,
                puzzle,
            });
        }
    }
    /** The puzzle ordinal the unlock rule is measured against (WP-65.F1). */
    get puzzle() {
        return this._puzzle;
    }
    /** Uses left this level — the full cap until one is spent. */
    usesLeft(id) {
        const definition = this.definitions.find((p) => p.id === id);
        if (!definition)
            return 0;
        return Math.max(0, definition.perLevelCap - (this._used.get(id) ?? 0));
    }
    /**
     * Module 90 — the open, unpaid power-ups whose price is held back, as seen by
     * a purchase of `forId` (a power-up never holds against its own charge). One
     * that a free use will cover holds nothing. Module 91 — every open one, not
     * just the first.
     */
    heldFor(forId) {
        return (this._deps.pendingCharge?.() ?? []).filter((id) => id !== forId && this.freeUsesLeft(id) === 0);
    }
    /** Module 90 — coins held back: the summed price of {@link heldFor}. */
    heldCoins(forId) {
        return this.heldFor(forId).reduce((sum, id) => sum + (this.definitions.find((p) => p.id === id)?.cost.coins ?? 0), 0);
    }
    /** Module 90 — the coins a purchase of `forId` may actually spend. */
    spendableCoins(forId) {
        return Math.max(0, this._deps.wallet.getBalance('coins') - this.heldCoins(forId));
    }
    /** Free uses remaining for this id — its own (`_freeUsed`) plus the shared ticket pool. */
    freeUsesLeft(id) {
        const own = this._ownFreeUsesLeft(id);
        return own === null ? 0 : own + this._bonusFree;
    }
    /** This id's own free grant left, or null for an unknown id. */
    _ownFreeUsesLeft(id) {
        const definition = this.definitions.find((p) => p.id === id);
        if (!definition)
            return null;
        return Math.max(0, definition.freeUses - (this._freeUsed.get(id) ?? 0));
    }
    /** Module 118 — add free uses to the shared pool (the daily reward's day 7). */
    grantBonusFreeUses(n) {
        if (Number.isInteger(n) && n > 0)
            this._bonusFree += n;
    }
    /** Module 118 — adopt the persisted shared pool. Idempotent. */
    restoreBonusFreeUses(n) {
        this._bonusFree = Number.isInteger(n) && n > 0 ? n : 0;
    }
    /** Module 118 — what `_commitState` persists as `bonusFreeUses`. */
    bonusFreeUses() {
        return this._bonusFree;
    }
    /** Adopt the persisted consumed-free-uses record (Module 63). Idempotent. */
    restoreFreeUses(consumed) {
        this._freeUsed.clear();
        for (const [id, n] of Object.entries(consumed ?? {})) {
            if (Number.isInteger(n) && n > 0)
                this._freeUsed.set(id, n);
        }
    }
    /** The record `_commitState` persists — only ids with something consumed. */
    /**
     * Module 129 (WP-129.3) — uses spent in THIS attempt, for the save, so the
     * once-per-puzzle cap survives a reload (Economy_Rules.md rule 3).
     */
    usedThisLevel() {
        return Object.fromEntries([...this._used.entries()].filter(([, n]) => n > 0));
    }
    /**
     * Module 129 (WP-129.3) — put back a resumed attempt's uses. Call after
     * `startLevel` (which clears them). Entries are clamped to each power-up's
     * per-puzzle cap; an unknown id is dropped.
     */
    restoreUsedThisLevel(used) {
        this._used.clear();
        for (const [id, n] of Object.entries(used ?? {})) {
            const definition = this.definitions.find((p) => p.id === id);
            if (!definition || !Number.isInteger(n) || n <= 0)
                continue;
            this._used.set(id, Math.min(n, definition.perLevelCap));
        }
    }
    freeUsesConsumed() {
        return Object.fromEntries([...this._freeUsed.entries()].filter(([, n]) => n > 0));
    }
    /**
     * The live state of one power-up, or `null` for an id the config doesn't
     * define.
     *
     * `board` is optional and omitting it reproduces the pre-Module-70 answer
     * exactly — the Studio, fixtures and previews have no board, and a caller
     * that has one is telling this controller it may also refuse `nothingToDo`.
     */
    status(id, board) {
        const definition = this.definitions.find((p) => p.id === id);
        return definition ? this._statusOf(definition, board) : null;
    }
    /** Every power-up's live state, in config order — what the bar renders. */
    statuses(board) {
        return this.definitions.map((d) => this._statusOf(d, board));
    }
    /**
     * Spend one use: enforce unlock → cap → funds, charge, count it, report it.
     * The caller applies the effect only on `ok` and calls `complete()` when the
     * effect finishes (immediately for Hammer/Color Delete, after the chosen block
     * leaves for Rainbow Gates).
     */
    use(id) {
        const definition = this.definitions.find((p) => p.id === id);
        if (!definition)
            return R.err('unknown');
        if (!isPowerupUnlocked(definition, this._puzzle))
            return R.err('locked');
        if (this.usesLeft(id) <= 0) {
            this._deps.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_CAPPED, {
                powerup_id: id,
                level_id: this._level,
                per_level_cap: definition.perLevelCap,
            });
            return R.err('capped');
        }
        // Module 63 (WP-63.E5) — a free grant is spent BEFORE the wallet is asked,
        // and consumed only on a use that actually happens: the effect has already
        // said it has something to do by the time the plugin gets here.
        const free = this.freeUsesLeft(id) > 0;
        if (free) {
            if ((this._ownFreeUsesLeft(id) ?? 0) > 0) {
                this._freeUsed.set(id, (this._freeUsed.get(id) ?? 0) + 1);
            }
            else {
                this._bonusFree -= 1;
            }
        }
        else {
            const charged = this._charge(definition);
            if (!charged.ok)
                return R.err(charged.error);
        }
        this._used.set(id, (this._used.get(id) ?? 0) + 1);
        const usesLeft = this.usesLeft(id);
        this._deps.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_ACTIVATED, {
            powerup_id: id,
            level_id: this._level,
            cost_coins: free ? 0 : definition.cost.coins,
            cost_gems: free ? 0 : definition.cost.gems,
            uses_left: usesLeft,
            free,
            free_uses_left: this.freeUsesLeft(id),
        });
        return R.ok({
            id,
            cost: free ? { coins: 0, gems: 0 } : { ...definition.cost },
            usesLeft,
            free,
        });
    }
    /**
     * The effect finished. Separate from `use()` because Rainbow Gates activates
     * now and completes when the player's chosen block actually leaves — one event
     * could not describe both ends of that.
     */
    complete(id, details = {}) {
        if (!this.definitions.some((p) => p.id === id))
            return;
        this._deps.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_COMPLETED, {
            powerup_id: id,
            level_id: this._level,
            ...details,
        });
    }
    // ─── Private ──────────────────────────────────────────────────────────────
    _statusOf(definition, board) {
        const unlocked = isPowerupUnlocked(definition, this._puzzle);
        const usesLeft = this.usesLeft(definition.id);
        const freeUsesLeft = this.freeUsesLeft(definition.id);
        // A free use is affordable by definition — the wallet is not consulted.
        const affordable = freeUsesLeft > 0 || this._canAfford(definition.cost, definition.id);
        // Module 70 — ordered ABOVE `insufficientFunds` on purpose. On a board this
        // power-up cannot act on, quoting a price is worse than saying nothing: it
        // tells the player to go and buy something that would still do nothing.
        const usefulHere = board === undefined || effectAvailableOn(definition.id, board);
        const refusal = !unlocked
            ? 'locked'
            : usesLeft <= 0
                ? 'capped'
                : !usefulHere
                    ? 'nothingToDo'
                    : !affordable
                        ? 'insufficientFunds'
                        : null;
        return {
            id: definition.id,
            cost: { ...definition.cost },
            perLevelCap: definition.perLevelCap,
            unlockPuzzle: definition.unlockPuzzle,
            unlocked,
            usesLeft,
            affordable,
            usable: refusal === null,
            refusal,
            freeUsesLeft,
        };
    }
    _canAfford(cost, forId) {
        const { wallet } = this._deps;
        return this.spendableCoins(forId) >= cost.coins && wallet.getBalance('gems') >= cost.gems;
    }
    /** Charge both currencies or neither. */
    _charge(definition) {
        const { coins, gems } = definition.cost;
        if (!this._canAfford(definition.cost, definition.id))
            return R.err('insufficientFunds');
        const reasonId = powerupReasonId(definition.id);
        const { wallet } = this._deps;
        // A zero amount is never debited — see the module header.
        if (coins > 0) {
            const paid = wallet.debit('coins', coins, reasonId);
            if (!paid.ok)
                return R.err('insufficientFunds');
        }
        if (gems > 0) {
            const paid = wallet.debit('gems', gems, reasonId);
            if (!paid.ok) {
                // Roll the coins back: the balance check above passed, so a failure here
                // is a race or a server rejection, and the player must not be left short
                // for an effect that never ran.
                if (coins > 0)
                    wallet.credit('coins', coins, reasonId);
                return R.err('insufficientFunds');
            }
        }
        return R.ok(undefined);
    }
}
